/**
 * DailyBusinessScore — the signature iSpeedToClose visual.
 *
 * A circular gauge that summarizes the overall health of the user's
 * business as a single 0–100 score, computed daily. Meant to sit at
 * the top of the dashboard and be replicated at reduced size across
 * module pages so a single screenshot immediately reads as
 * "iSpeedToClose".
 *
 * Design:
 *   • Two concentric SVG arcs — track (grey) + progress (amber→purple gradient).
 *   • Center: giant tabular number with a trend delta chip.
 *   • Below (full variant): 4 breakdown chips (Pipeline / Quality / AI / Cash Flow).
 *   • Subtle rotating conic-gradient shimmer behind the ring gives a "live" feel.
 *   • Two sizes:
 *       size="lg" → 220px hero ring w/ breakdown chips + trend chip
 *       size="sm" → 96px mini ring, no chips (for use on module stubs)
 *
 * Motion respects prefers-reduced-motion (CSS).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Sparkles } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Helper — grade label + color band from score
// ─────────────────────────────────────────────────────────────
export function gradeFor(score) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  if (s >= 88) return { label: "Elite",   color: "#059669" };
  if (s >= 72) return { label: "Strong",  color: "#10B981" };
  if (s >= 58) return { label: "Steady",  color: "#F59E0B" };
  if (s >= 45) return { label: "At risk", color: "#EA580C" };
  return { label: "Critical", color: "#DC2626" };
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
export default function DailyBusinessScore({
  score = 0,
  trend = null,          // delta vs yesterday, e.g. +3 or -2
  breakdown = [],        // [{ label, value, color }]
  size = "lg",           // "lg" | "sm"
  label = "Daily Business Score",
  testid = "daily-business-score",
}) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const grade = gradeFor(s);

  // Animate previous → s on mount and whenever the score prop changes,
  // instead of restarting from 0 every time.
  const [displayed, setDisplayed] = useState(0);
  const prevRef = useRef(0);
  const rafRef = useRef();
  useEffect(() => {
    const reduce = typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDisplayed(s); prevRef.current = s; return; }
    const from = prevRef.current;
    const to = s;
    const start = performance.now();
    const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = from + (to - from) * eased;
      setDisplayed(val);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else prevRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [s]);

  // SVG geometry
  const dim = size === "lg" ? 220 : 96;
  const stroke = size === "lg" ? 14 : 8;
  const r = (dim / 2) - stroke;
  const cx = dim / 2;
  const cy = dim / 2;
  const circumference = 2 * Math.PI * r;
  const dash = (displayed / 100) * circumference;

  const uid = useMemo(
    () => "dbs-" + Math.random().toString(36).slice(2, 8), []);

  const numberFontSize = size === "lg" ? "text-[64px]" : "text-[26px]";
  const labelFontSize  = size === "lg" ? "text-[11px]" : "text-[9px]";

  return (
    <div
      data-testid={testid}
      className={`inline-flex ${size === "lg" ? "flex-col" : "flex-row"} items-center gap-3`}
    >
      <div className="relative"
           style={{ width: dim, height: dim }}
           aria-label={`${label}: ${Math.round(s)} out of 100`}>
        {/* Ambient glow behind the ring */}
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              `radial-gradient(circle at center, ${grade.color}22 0%, transparent 60%)`,
            filter: "blur(6px)",
          }}
        />
        {/* Rotating shimmer (very subtle) */}
        {size === "lg" && (
          <div
            aria-hidden="true"
            className="absolute inset-2 rounded-full pointer-events-none dbs-rotate"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(245,158,11,0.14), rgba(139,92,246,0.05) 40%, transparent 70%, rgba(245,158,11,0.14) 100%)",
              WebkitMaskImage:
                "radial-gradient(circle, transparent 62%, black 66%)",
              maskImage:
                "radial-gradient(circle, transparent 62%, black 66%)",
            }}
          />
        )}

        <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} className="relative">
          <defs>
            <linearGradient id={`${uid}-grad`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0"  stopColor="#F59E0B" />
              <stop offset="55%" stopColor="#EF4444" />
              <stop offset="1"  stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          {/* Track */}
          <circle
            cx={cx} cy={cy} r={r}
            stroke="#EEF2F7"
            strokeWidth={stroke}
            fill="none"
          />
          {/* Progress */}
          <circle
            cx={cx} cy={cy} r={r}
            stroke={`url(#${uid}-grad)`}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: "stroke-dasharray 60ms linear" }}
          />
          {/* End cap dot */}
          {displayed > 2 && (
            <circle
              cx={cx + r * Math.cos((-Math.PI / 2) + (displayed / 100) * 2 * Math.PI)}
              cy={cy + r * Math.sin((-Math.PI / 2) + (displayed / 100) * 2 * Math.PI)}
              r={stroke / 2 + 1}
              fill="white"
              stroke={grade.color}
              strokeWidth="2"
            />
          )}
        </svg>

        {/* Center number */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={`${numberFontSize} font-black tabular-nums leading-none text-slate-900`}
               data-testid={`${testid}-value`}>
            {Math.round(displayed)}
          </div>
          {size === "lg" && (
            <>
              <div className={`${labelFontSize} uppercase tracking-widest font-bold mt-1`}
                   style={{ color: grade.color }}
                   data-testid={`${testid}-grade`}>
                {grade.label}
              </div>
              {trend !== null && trend !== undefined && (
                <div
                  data-testid={`${testid}-trend`}
                  className={`mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    trend >= 0
                      ? "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/30"
                      : "bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/30"
                  }`}
                >
                  {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {trend >= 0 ? "+" : ""}{trend} vs yesterday
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Full-variant breakdown chips */}
      {size === "lg" && breakdown.length > 0 && (
        <div className="mt-1 w-full max-w-[260px] space-y-1.5"
             data-testid={`${testid}-breakdown`}>
          {breakdown.map((b) => (
            <div key={b.label}
                 className="flex items-center gap-2 text-[11px]">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: b.color }}
              />
              <span className="text-slate-600 flex-1 truncate">
                {b.label}
              </span>
              <span className="font-bold tabular-nums" style={{ color: b.color }}>
                {b.value}
              </span>
            </div>
          ))}
          <div className="pt-1 mt-1 border-t border-slate-100 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-400">
            <Sparkles className="w-3 h-3 text-amber-500" />
            iSpeed AI · signature score
          </div>
        </div>
      )}

      {size === "sm" && label && (
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
            {label}
          </div>
          <div className="text-[11px] font-semibold" style={{ color: grade.color }}>
            {grade.label}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Given executive metrics + briefing shapes we already have, compute a
 * plausible 0–100 score with 4 breakdown components. Deterministic so
 * two visits in the same session see the same number.
 */
export function computeDailyScore({ kpis = {}, portfolio = {}, opps = 0 }) {
  const pipeline    = Number(kpis.pipeline_value) || 0;
  const profit      = Number(kpis.net_profit) || 0;
  const capRate     = Number(portfolio.cap_rate_pct) || 0;
  const assignments = Number(kpis.assignments) || 0;

  // Sub-scores (0-100) — each has a 40 floor so an active tenant is
  // never labeled "critical" unless they truly have nothing running.
  const pipeSub  = Math.round(40 + Math.min(60, (pipeline / 10_000_000) * 60));
  const qualSub  = Math.round(40 + Math.min(60, (assignments / 100) * 60));
  const aiSub    = Math.round(50 + Math.min(50, opps * 6));
  const cashSub  = Math.round(40 + Math.min(60,
                    (profit / 1_500_000) * 40 + capRate * 2));

  // Weighted total: pipeline 30%, quality 20%, AI 25%, cash 25%
  const total = Math.round(
    pipeSub * 0.30 +
    qualSub * 0.20 +
    aiSub   * 0.25 +
    cashSub * 0.25,
  );

  return {
    score: Math.max(30, Math.min(100, total)),
    breakdown: [
      { label: "Pipeline momentum",  value: pipeSub, color: "#F59E0B" },
      { label: "Deal quality",       value: qualSub, color: "#EF4444" },
      { label: "AI recommendations", value: aiSub,   color: "#8B5CF6" },
      { label: "Cash-flow health",   value: cashSub, color: "#10B981" },
    ],
    trend: 3,   // TODO: replace with real delta from backend when available
  };
}
